export type CapabilityAvailability = 'AVAILABLE' | 'READ_ONLY' | 'UNAVAILABLE'

export type CapabilityExecution = 'DIRECT' | 'APPROVAL_REQUIRED' | 'NONE'

export type CapabilityBacking = 'LIVE' | 'SURROGATE' | 'NONE'

export type ReadPhysicalCardLimitConstraints = {
  currency: string
}

export type ApplyPhysicalCardLimitChangeConstraints = {
  currency: string
  requiresIdempotencyKey: boolean
  requiresExpectedVersion: boolean
}

export type MccRestrictionRequirements = {
  currency: string
  supportedModes: CardManagementMccRestrictionMode[]
  maxExceptionMccGroupIds: number
  excludedMccGroupIds: string[]
  requiresIdempotencyKey: boolean
  requiresExpectedVersion: boolean
}

type CapabilityDecisionTransportFields = {
  availability: CapabilityAvailability
  execution: CapabilityExecution
  backing: CapabilityBacking
  reasonCode: string | null
}

/** Actions whose public contract has no request-time capability constraints. */
export type CardManagementConstraintFreeAction =
  | 'VIEW_PHYSICAL_CARD_MANAGEMENT'
  | 'FREEZE_PHYSICAL_CARD'
  | 'UNFREEZE_PHYSICAL_CARD'
  | 'REQUEST_PHYSICAL_CARD_UNLOCK'
  | 'READ_PHYSICAL_CARD_SECURE_DETAILS'
  | 'MODIFY_PHYSICAL_CARD_NICKNAME'
  | 'REPLACE_PHYSICAL_CARD'
  | 'REPLACE_LOST_OR_STOLEN_PHYSICAL_CARD'
  | 'REPORT_PHYSICAL_CARD_FRAUD'
  | 'CANCEL_PHYSICAL_CARD'
  | 'READ_PHYSICAL_CARD_MCC'
  | 'READ_PHYSICAL_CARD_BILLING'
  | 'MANAGE_PHYSICAL_CARD_BILLING'
  | 'READ_PHYSICAL_CARD_ACTIVITY'
  | 'READ_PHYSICAL_CARD_STATEMENTS'
  | 'READ_PHYSICAL_CARD_PAYMENT_SUMMARY'
  | 'READ_PHYSICAL_CARD_PAYMENTS'
  | 'READ_PHYSICAL_CARD_PAYMENT_CHANNELS'
  | 'MANAGE_PHYSICAL_CARD_PAYMENT_CHANNELS'
  | 'READ_PHYSICAL_CARD_GEOFENCE'
  | 'MANAGE_PHYSICAL_CARD_GEOFENCE'
  | 'READ_PHYSICAL_CARD_COMPANY_LINE'

export type CapabilityDecisionResponse = CapabilityDecisionTransportFields &
  (
    | {
        action: 'READ_PHYSICAL_CARD_LIMIT'
        constraints: ReadPhysicalCardLimitConstraints | null
      }
    | {
        action: 'APPLY_PHYSICAL_CARD_LIMIT_CHANGE'
        constraints: ApplyPhysicalCardLimitChangeConstraints | null
      }
    | {
        action: 'MANAGE_PHYSICAL_CARD_MCC'
        constraints: MccRestrictionRequirements | null
      }
    | {
        action: CardManagementConstraintFreeAction
        constraints: null
      }
  )

export type CardManagementAction = CapabilityDecisionResponse['action']

/**
 * Whether the server says this actor may perform `action` on this card.
 *
 * Keyed by `action`, never by array index — the contract emits the entries in a
 * documented order but explicitly requires consumers not to depend on it.
 *
 * Fails closed on anything that is not an outright AVAILABLE: undefined
 * capabilities (the query is pending, errored, or disabled), a missing entry (an
 * older server that predates the action), and READ_ONLY alike. Callers that OR
 * this with a client flag therefore degrade to the flag rather than to an
 * accidental grant.
 */
export const isCardManagementActionAvailable = (
  capabilities: CapabilityDecisionResponse[] | undefined,
  action: CardManagementAction,
): boolean =>
  capabilities?.find((capability) => capability.action === action)
    ?.availability === 'AVAILABLE'

/**
 * Whether this card may see the Card Management surfaces at all.
 *
 * `VIEW_PHYSICAL_CARD_MANAGEMENT` is the server's demo-enrollment verdict, and
 * it is the precondition for every other card-management action: the API
 * resolves it from the `PHYSICAL_CARD_MANAGEMENT_DEMO_ENABLED` kill-switch ANDed
 * with (this card on the card allowlist OR its org on the org allowlist), and
 * emits UNAVAILABLE + `FEATURE_DISABLED` otherwise. Enrolling an org lights up
 * every card it owns, present and future — which is what makes this usable as
 * "does this org have the demo".
 *
 * Accepts READ_ONLY as well as AVAILABLE, unlike the strict
 * {@link isCardManagementActionAvailable}. This is a VISIBILITY question, and
 * READ_ONLY means "you may see this, but not change it" — exactly the surface a
 * read-only viewer should still get. Every mutation inside those surfaces is
 * gated on its own action anyway, so admitting READ_ONLY here grants nothing.
 *
 * Still fails closed on `undefined` (query pending, errored, or disabled) and on
 * a response that never mentions the action.
 */
export const isCardManagementViewAvailable = (
  capabilities: CapabilityDecisionResponse[] | undefined,
): boolean => {
  const view = capabilities?.find(
    (capability) => capability.action === 'VIEW_PHYSICAL_CARD_MANAGEMENT',
  )
  return (
    view?.availability === 'AVAILABLE' || view?.availability === 'READ_ONLY'
  )
}

/**
 * The server's durable reason for a denied action.
 *
 * Needed because `isCardManagementActionAvailable` fails closed for EVERY
 * non-AVAILABLE verdict, so a caller cannot tell "your role cannot do this"
 * from "this card is not enrolled" / "the contract is unresolved". Telling an
 * Admin to go ask an Admin is the failure mode this prevents.
 */
export const cardManagementActionReasonCode = (
  capabilities: CapabilityDecisionResponse[] | undefined,
  action: CardManagementAction,
): string | undefined =>
  capabilities?.find((capability) => capability.action === action)
    ?.reasonCode ?? undefined

export interface CardManagementCapabilitiesResponse {
  creditCardId: string
  capabilities: CapabilityDecisionResponse[]
}

/** Masked secure-details response currently served in stage and dev. */
export interface CardManagementCardDetailsResponse {
  creditCardId: string
  pan: string
  last4: string
  expirationMonth: number
  expirationYear: number
  securityCode: string
}

export type AvailableSpendCalculation =
  | 'PARTNER_REPORTED'
  | 'PERMANENT_CONTROL_MINUS_BALANCE'

export type CardLimitsProvenance = 'SURROGATE_PARTNER' | 'LIVE_PARTNER'

export interface CardLimitsResponse {
  creditCardId: string
  currency: string
  permanentLimit: number
  availableSpend: number
  currentBalance: number
  version: number
  availableSpendCalculation: AvailableSpendCalculation
  provenance: CardLimitsProvenance
  sourceAsOf: string
}

export interface LimitChangeRequest {
  kind: 'PERMANENT'
  permanentLimit: number
  currency: string
  expectedVersion: number
}

export interface LimitChangeResponse {
  operationId: string
  status: 'SUCCEEDED'
  limits: CardLimitsResponse
}

export type CardManagementMccRestrictionMode =
  | 'ALLOW_ALL_EXCEPT'
  | 'BLOCK_ALL_EXCEPT'

export type CardManagementMccRestrictionProvenance =
  | 'SURROGATE_PARTNER'
  | 'LIVE_PARTNER'

export interface CardManagementMccRestrictionsResponse {
  creditCardId: string
  mode: CardManagementMccRestrictionMode
  exceptionMccGroupIds: string[]
  version: number
  provenance: CardManagementMccRestrictionProvenance
  sourceAsOf: string
}

export interface ReplaceCardManagementMccRestrictionsRequest {
  mode: CardManagementMccRestrictionMode
  exceptionMccGroupIds: string[]
  expectedVersion: number
}

export interface ReplaceCardManagementMccRestrictionsResponse {
  operationId: string
  status: 'SUCCEEDED'
  mccRestrictions: CardManagementMccRestrictionsResponse
}

export interface CardManagementErrorResponse {
  error: string
  errorCode: string
}

export type PhysicalCardManagementStatus =
  | 'PENDING_ISSUANCE'
  | 'ACTIVE'
  | 'FROZEN'
  | 'LOCKED'
  | 'REPLACEMENT_PENDING'
  | 'CANCELED'
  | 'FAILED'

export interface CardManagementStatusResponse {
  creditCardId: string
  status: PhysicalCardManagementStatus
  version: number
}

export interface CardManagementStatusChangeRequest {
  expectedVersion: number
}

export interface CardManagementStatusChangeResponse {
  operationId: string
  status: 'PENDING' | 'SUCCEEDED'
  card: CardManagementStatusResponse
}

export type CreatePermanentLimitChangeInput = {
  creditCardId: string
  request: LimitChangeRequest
  idempotencyKey: string
}

export type ReplaceCardManagementMccRestrictionsInput = {
  creditCardId: string
  request: ReplaceCardManagementMccRestrictionsRequest
  idempotencyKey: string
}

export type CreateCardManagementStatusChangeInput = {
  creditCardId: string
  request: CardManagementStatusChangeRequest
  idempotencyKey: string
}

/**
 * Same vocabulary as {@link CardLimitsProvenance}, kept as its own alias because
 * the two resources version and source independently — a shared name would
 * imply they move together.
 */
export type CardManagementProvenance = 'SURROGATE_PARTNER' | 'LIVE_PARTNER'

/**
 * Billing address at the Card Management transport boundary (EX-40624).
 *
 * `line2`, `province`, and `postal` are nullable *at this boundary specifically
 * because their requirements vary by country* — the server applies
 * country-specific address rules before accepting an update. Null is a real,
 * meaningful value here: on a complete-set PUT it CLEARS the field where the
 * selected country's rules permit. Do not collapse null to `''`, and do not
 * treat these three as universally optional in form validation.
 *
 * `country` is an uppercase ISO-3166 alpha-2 code. Text fields cap at 255.
 */
export interface CardManagementBillingAddress {
  line1: string
  line2: string | null
  city: string
  province: string | null
  postal: string | null
  country: string
}

export interface CardManagementBillingContactResponse {
  creditCardId: string
  nameLine1: string
  nameLine2: string | null
  billingAddress: CardManagementBillingAddress
  /** E.164: `+`, a non-zero country-code digit, 8–15 total digits. */
  mobilePhone: string
  version: number
  provenance: CardManagementProvenance
  /** Unix epoch **milliseconds** — unlike the limits snapshot's ISO-8601 string. */
  sourceAsOf: number
}

/**
 * PUT is a complete SET, not a patch: the client sends the entire current form
 * plus the `expectedVersion` from its latest GET. That is what makes an omitted
 * nullable field unambiguous rather than "leave it alone".
 */
export interface ReplaceCardManagementBillingContactRequest {
  nameLine1: string
  nameLine2: string | null
  billingAddress: CardManagementBillingAddress
  mobilePhone: string
  expectedVersion: number
}

/**
 * `status` is `SUCCEEDED` only. The contract fails an uncertain or partial
 * issuer outcome closed (`CARD-MGMT.13`, reconciliation) rather than reporting
 * it as a pending operation, so — unlike the status-change family — there is no
 * `PENDING` to model here.
 */
export interface ReplaceCardManagementBillingContactResponse {
  operationId: string
  status: 'SUCCEEDED'
  billingContact: CardManagementBillingContactResponse
}

export type ReplaceCardManagementBillingContactInput = {
  creditCardId: string
  request: ReplaceCardManagementBillingContactRequest
  idempotencyKey: string
}

/**
 * Company-line summary — a read-only projection of the line the card draws on.
 *
 * Deliberately NOT an overview aggregate: the frontend composes it with card
 * identity, `/limits`, and `/payments/summary` so one source's failure or
 * staleness cannot invalidate the others.
 *
 * `availableCredit` is SIGNED. Zero means no headroom; negative means the
 * authoritative source reports the line overdrawn or overcommitted. Clients
 * must display that state and must never clamp it to zero or recompute it from
 * card-level balances.
 */
export interface CardManagementCompanyLineResponse {
  creditCardId: string
  /** Display name only — the contract exposes no company-line identifier. */
  name: string
  /** Minor units, non-negative. */
  totalCredit: number
  /** Minor units, signed. Do NOT clamp. */
  availableCredit: number
  /** Both money fields always share this currency; the server fails closed otherwise. */
  currency: string
  provenance: CardManagementProvenance
  /** ISO-8601 instant. */
  sourceAsOf: string
}

/**
 * Payment channels.
 *
 * Four card-level controls, each independently versioned and independently
 * routed. A PUT changes EXACTLY ONE channel and fences on that channel's own
 * `version` — there is no whole-resource version, and a multi-channel request
 * with ambiguous partial application is deliberately not offered.
 */
export type PaymentChannel =
  | 'ONLINE_ECOMMERCE'
  | 'IN_STORE'
  | 'ATM'
  | 'MOBILE_WALLET'

/**
 * Per-channel availability.
 *
 * - `AVAILABLE` — known `enabled`, positive `version`; the actor may PUT.
 * - `READ_ONLY` — known state, but PUT returns 403. Render, do not offer.
 * - `UNAVAILABLE` — `enabled`, `version`, `provenance` and `sourceAsOf` are all
 *   null. Clients MUST NOT guess an on/off value; there is nothing to show but
 *   the reason.
 */
export type PaymentChannelAvailability =
  | 'AVAILABLE'
  | 'READ_ONLY'
  | 'UNAVAILABLE'

export interface PaymentChannelState {
  channel: PaymentChannel
  /** Null when UNAVAILABLE — never coerce to false, that is a real claim. */
  enabled: boolean | null
  availability: PaymentChannelAvailability
  /** Non-null unless AVAILABLE. Shared Card Management reason vocabulary. */
  reasonCode: string | null
  /** Null when UNAVAILABLE. This channel's OWN optimistic-concurrency fence. */
  version: number | null
  provenance: CardManagementProvenance | null
  /** Unix epoch milliseconds; null when UNAVAILABLE. */
  sourceAsOf: number | null
}

export interface CardManagementPaymentChannelsResponse {
  creditCardId: string
  /** Exactly one entry per public channel, in enum order. Key by `channel`. */
  channels: PaymentChannelState[]
}

/** Idempotent SET, not a toggle: retrying the same desired state cannot invert it. */
export interface SetPaymentChannelRequest {
  enabled: boolean
  expectedVersion: number
}

/**
 * `SUCCEEDED` only. Approved product semantics say policy changes apply
 * immediately, so there is no `PENDING`; an uncertain processor effect is
 * `CARD-MGMT.13` and must be reconciled before another mutation.
 */
export interface SetPaymentChannelResponse {
  operationId: string
  status: 'SUCCEEDED'
  paymentChannel: PaymentChannelState
}

export type SetPaymentChannelInput = {
  creditCardId: string
  channel: PaymentChannel
  request: SetPaymentChannelRequest
  idempotencyKey: string
}
/**
 * Geofence (EX-40620 / EX-40629).
 *
 * `permittedCountries` is an ALLOW-list. An available snapshot with an empty
 * list means the card is blocked worldwide — it emphatically does not mean
 * "everywhere is allowed", and no client may render it that way.
 *
 * `displayName` is server-authored presentation text and must never be sent
 * back in a mutation: the wire identity of a country is its `countryCode`.
 */
export interface CardManagementGeofenceCountry {
  /** Uppercase ISO 3166-1 alpha-2. */
  countryCode: string
  /** Server-authored, already localized. Display only — never sent back. */
  displayName: string
}

/**
 * Same three-state vocabulary as payment channels, and the same rule: an
 * UNAVAILABLE snapshot nulls `permittedCountries`, `version`, `provenance` and
 * `sourceAsOf` together. Clients must not guess an empty list in its place —
 * "blocked worldwide" is a real, different claim.
 */
export interface CardManagementGeofenceResponse {
  creditCardId: string
  availability: CapabilityAvailability
  /** Non-null unless AVAILABLE. Shared Card Management reason vocabulary. */
  reasonCode: string | null
  /** Null only when UNAVAILABLE. Empty array = blocked worldwide. */
  permittedCountries: CardManagementGeofenceCountry[] | null
  /** Null when UNAVAILABLE. Optimistic-concurrency fence for the next change. */
  version: number | null
  provenance: CardManagementProvenance | null
  /** Unix epoch **milliseconds**; null when UNAVAILABLE. */
  sourceAsOf: number | null
}

/**
 * Card-scoped country catalogue search.
 *
 * Deliberately not the app's own `/countries` reference list: the server only
 * returns countries that are a valid selection *for this card*, and authors the
 * display names. Sourcing the picker locally would offer codes the mutation
 * then rejects with `CARD-MGMT.23`.
 */
export type SearchCardManagementGeofenceCountriesInput = {
  creditCardId: string
  /**
   * Case-insensitive display-name/code search, max 100 characters **trimmed**.
   * A whitespace-padded non-empty value is `CARD-MGMT.2`, so callers send a
   * trimmed value or omit the param entirely.
   */
  query?: string
  /** Defaults to 50 server-side; capped at 100. */
  limit?: number
  /** Opaque, scoped to card + query. Never parse or combine cursors. */
  before?: string
}

export interface SearchCardManagementGeofenceCountriesResponse {
  creditCardId: string
  items: CardManagementGeofenceCountry[]
  /** Opaque; `null` is terminal. */
  nextCursor: string | null
}

export type CardManagementGeofenceChange = 'ADD' | 'REMOVE'

/**
 * One country per request. The contract deliberately offers no multi-country
 * mutation rather than inventing partial-success semantics, which matches the
 * design's immediate add/remove interaction.
 *
 * Adding an already-permitted country (or removing an absent one) is a
 * successful no-op while `expectedVersion` still matches, and the returned
 * snapshot's version need not advance.
 */
export interface ChangeCardManagementGeofenceCountryRequest {
  change: CardManagementGeofenceChange
  countryCode: string
  expectedVersion: number
}

/**
 * `SUCCEEDED` only — approved policy changes are immediate. The returned
 * snapshot is guaranteed AVAILABLE, so it can be written straight into the
 * cache as the authoritative post-write state.
 */
export interface ChangeCardManagementGeofenceCountryResponse {
  operationId: string
  status: 'SUCCEEDED'
  geofence: CardManagementGeofenceResponse
}

export type ChangeCardManagementGeofenceCountryInput = {
  creditCardId: string
  request: ChangeCardManagementGeofenceCountryRequest
  idempotencyKey: string
}

export type CardReplacementReason = 'DAMAGED' | 'LOST_OR_STOLEN'

export interface CreateCardReplacementRequest {
  reason: CardReplacementReason
  expectedVersion: number
}

export interface CreateCardLifecycleRequest {
  expectedVersion: number
}

export type CardManagementLifecycleOperationStatus =
  | 'PENDING_APPROVAL'
  | 'QUEUED'
  | 'PROCESSING'
  | 'SUCCEEDED'
  | 'FAILED'
  | 'CANCELED'

export type CardManagementLifecycleOperationType =
  | 'UPDATE_CARD_NICKNAME'
  | 'REPLACE_DAMAGED_CARD'
  | 'REPLACE_LOST_OR_STOLEN_CARD'
  | 'REPORT_CARD_FRAUD'
  | 'CANCEL_CARD'
  | 'REQUEST_CARD_UNLOCK'
  | 'ISSUE_CARD'

export interface CardManagementLifecycleOperationResult {
  resultingCreditCardId: string
  resultingCardVersion: number
}

export interface CardManagementLifecycleOperationResponse {
  operationId: string
  type: CardManagementLifecycleOperationType
  status: CardManagementLifecycleOperationStatus
  subjectCreditCardId: string | null
  result: CardManagementLifecycleOperationResult | null
  failureCode: string | null
  createdAt: string
  updatedAt: string
}

type CreateCardLifecycleInput<Request> = {
  creditCardId: string
  request: Request
  idempotencyKey: string
}

export type CreateCardReplacementRequestInput =
  CreateCardLifecycleInput<CreateCardReplacementRequest>

export type CreateCardFraudReportInput =
  CreateCardLifecycleInput<CreateCardLifecycleRequest>

export type CreateCardCancellationRequestInput =
  CreateCardLifecycleInput<CreateCardLifecycleRequest>
/* -------------------------------------------------------------------------- */
/* Activity (EX-40583)                                                        */
/* -------------------------------------------------------------------------- */

/**
 * Card Management read contracts for Activity, Statements, and Payments.
 *
 * These mirror the frozen extend-api schemas field-for-field
 * (`docs/card-management/{activity,statements,payments}-api-contract.md`). Two
 * wire conventions differ from the rest of this file and must not be
 * "normalized" here:
 *
 * - Date-only fields (`periodStart`, `periodEnd`, `dueDate`, `postedOn`) arrive
 *   as `[year, month, day]` NUMBER ARRAYS, not ISO-8601 strings. `month` is
 *   1-based, unlike `Date`.
 * - Instants (`closedAt`, `snapshotAt`, `sourceAsOf`, `occurredAt`) arrive as
 *   Unix-epoch MILLISECONDS.
 *
 * Cursors are opaque and resource-scoped: never parse, construct, persist as
 * domain data, or carry one across cards or collections.
 */

/** `[year, month, day]`, month 1-based. The wire form for every date-only field. */
export type CardManagementLocalDate = readonly [number, number, number]

/** Only `USER` is returned today; the contract names `SYSTEM` as a future case. */
export type CardManagementActivityActorType = 'USER' | 'SYSTEM'

export type CardManagementActivityOutcome = 'SUCCEEDED' | 'FAILED'

export interface CardManagementActivityActorResponse {
  type: CardManagementActivityActorType
  displayName: string
  /** Explicitly `null` when absent — render `initials` instead. */
  avatarUrl: string | null
  initials: string
}

export interface CardManagementActivityResponse {
  id: string
  actor: CardManagementActivityActorResponse
  /** Server-authored. Never reconstruct or translate client-side. */
  message: string
  /** Server-authored. Explicitly `null` when absent. */
  detail: string | null
  outcome: CardManagementActivityOutcome
  /** Unix epoch MILLISECONDS. */
  occurredAt: number
}

export interface CardManagementActivityPageResponse {
  /** Echoed so a client can reject a response cached under the wrong card. */
  creditCardId: string
  /** Newest first. */
  items: CardManagementActivityResponse[]
  /** `null` means this page is terminal. */
  nextCursor: string | null
}

/* -------------------------------------------------------------------------- */
/* Statements (EX-40584)                                                      */
/* -------------------------------------------------------------------------- */

export type CardManagementStatementProvenance =
  | 'SURROGATE_PARTNER'
  | 'LIVE_PARTNER'

export type CardManagementStatementStatus =
  | 'DUE'
  | 'PARTIALLY_PAID'
  | 'PAID'
  | 'PAST_DUE'

export type CardManagementStatementDocumentFormat = 'PDF' | 'XLSX' | 'CSV'

/**
 * Public document locations published with statement history.
 *
 * The API returns an empty object when document hosting is not configured, so
 * every format key is optional at the frontend boundary. A missing key means
 * that format is unavailable; callers must never construct a replacement URL.
 */
export type CardManagementStatementDocumentUrls = Partial<
  Record<CardManagementStatementDocumentFormat, string>
>

export interface CardManagementCurrentCycleResponse {
  creditCardId: string
  periodStart: CardManagementLocalDate
  periodEnd: CardManagementLocalDate
  /** IANA zone governing the date-only billing boundaries above. */
  billingTimeZone: string
  /** SIGNED minor units — negative is a credit. Not an amount due. */
  currentCycleAmount: number
  currency: string
  provenance: CardManagementStatementProvenance
  /** Unix epoch MILLISECONDS. */
  sourceAsOf: number
}

export interface CardManagementStatementResponse {
  /** Durable Extend-owned id — the one used by the line-item route. */
  id: string
  /** Opaque issuer reference. Never send it back as identity. */
  issuerStatementId: string | null
  periodStart: CardManagementLocalDate
  periodEnd: CardManagementLocalDate
  billingTimeZone: string
  /** Unix epoch MILLISECONDS. */
  closedAt: number
  dueDate: CardManagementLocalDate
  /** SIGNED net statement activity at close, minor units. */
  statementAmount: number
  /** Non-negative minor units. */
  amountOriginallyDue: number
  /** Non-negative minor units still unresolved. */
  currentAmountDue: number
  /** Non-negative minor units successfully applied, net of reversals. */
  amountPaid: number
  currency: string
  status: CardManagementStatementStatus
  documentUrls?: CardManagementStatementDocumentUrls
}

export interface CardManagementStatementPageResponse {
  creditCardId: string
  /** Newest first (`closedAt DESC, id DESC`). Closed cycles only. */
  items: CardManagementStatementResponse[]
  nextCursor: string | null
  /** Unix epoch MILLISECONDS. The logical snapshot every cursor page reads. */
  snapshotAt: number
}

export interface CardManagementStatementItemResponse {
  id: string
  /** Issuer presentation order; the list is not re-sorted by date. */
  sequence: number
  postedOn: CardManagementLocalDate
  description: string
  /** SIGNED minor units — negative is a credit/refund line. */
  amount: number
  currency: string
}

export interface CardManagementStatementItemPageResponse {
  creditCardId: string
  statementId: string
  items: CardManagementStatementItemResponse[]
  nextCursor: string | null
  snapshotAt: number
}

/* -------------------------------------------------------------------------- */
/* Payments (EX-40585)                                                        */
/* -------------------------------------------------------------------------- */

export type CardManagementNextPaymentDueStatus =
  | 'PAID'
  | 'DUE'
  /** Legacy value kept until the expanded summary API is fully deployed. */
  | 'PAST_DUE'
  | 'OVERDUE'
  | 'FAILED'

export type CardManagementPaymentType =
  | 'BANK_TRANSFER'
  | 'CHECK'
  | 'WIRE'
  | 'OTHER'

/**
 * Payment-record status. `PARTIALLY_PAID` is deliberately absent: partial
 * payment is a STATEMENT-level condition, not a payment status.
 */
export type CardManagementPaymentStatus =
  | 'PENDING'
  | 'PAID'
  | 'FAILED'
  | 'REVERSED'

export interface CardManagementNextPaymentDueResponse {
  /** Present only when the amount maps unambiguously to one closed statement. */
  statementId: string | null
  /** Non-negative minor units. */
  amountDue: number
  dueDate: CardManagementLocalDate
  currency: string
  /** The SERVER decides `OVERDUE` (legacy: `PAST_DUE`), not our clock. */
  status: CardManagementNextPaymentDueStatus
}

export interface CardManagementPaymentSummaryResponse {
  creditCardId: string
  /** `null` when the issuer/integrator supplies no usable next-payment pair. */
  nextPaymentDue: CardManagementNextPaymentDueResponse | null
  provenance: CardManagementStatementProvenance
  sourceAsOf: number
}

export interface CardManagementPaymentResponse {
  id: string
  statementId: string | null
  /** Non-negative minor units. */
  amount: number
  currency: string
  type: CardManagementPaymentType
  status: CardManagementPaymentStatus
  /** Unix epoch MILLISECONDS. Immutable effective time; status changes never reorder it. */
  occurredAt: number
}

export interface CardManagementPaymentPageResponse {
  creditCardId: string
  /** Newest first (`occurredAt DESC, id DESC`). */
  items: CardManagementPaymentResponse[]
  /** `null` is the SOLE terminal signal — the final page may still have items. */
  nextCursor: string | null
  snapshotAt: number
}

/**
 * Marks the failure raised when the payments feed keeps handing back empty
 * pages with a cursor still on them.
 *
 * A tagged plain `Error` rather than an `Error` subclass, and deliberately so:
 * this package compiles to ES5 (`tsconfig.json` `"target": "es5"`), where
 * extending a built-in breaks the prototype chain and `instanceof` on the
 * subclass is ALWAYS false. A retry policy keyed on `instanceof` would compile,
 * typecheck, and silently never fire. The tag also matches how this layer
 * already discriminates failures — `isUnprovisioned` duck-types `status` the
 * same way.
 *
 * No HTTP `status` is attached, on purpose: this is not a response, and
 * `isUnprovisioned` must not mistake it for one and resolve it to the demo
 * surface.
 */
export const PAGING_ANOMALY_TAG = 'isCardManagementPagingAnomaly'

/**
 * Whether a failure is the empty-paging anomaly above.
 *
 * Exported for the read-retry policy: re-walking is another six serialized
 * requests per attempt to reach the identical verdict, on a page the user is
 * waiting on.
 */
export const isCardManagementPagingAnomaly = (error: unknown): boolean =>
  error !== null &&
  typeof error === 'object' &&
  PAGING_ANOMALY_TAG in error &&
  (error as Record<string, unknown>)[PAGING_ANOMALY_TAG] === true

/** Shared page request for every cursor-paginated Card Management read. */
export interface CardManagementPageInput {
  creditCardId: string
  /** 1..100. Anything else is `400 / CARD-MGMT.2`. */
  limit?: number
  /** Opaque cursor from the previous page's `nextCursor`. */
  before?: string
}
